/**
 * Query keys, in one place.
 *
 * TanStack Query caches and invalidates by key, so a key typed inline at a
 * call site is a cache entry nothing else can invalidate. Keeping them here
 * means an invalidation is a compile-time reference rather than a string two
 * files agreed on by luck.
 *
 * FRONTEND_DESIGN §6.2 constrains what may be cached at all: "Cache only read
 * responses in memory. A full refresh reconstructs from the backend." No key
 * below may ever be persisted to local storage — §6.2's last row is absolute
 * on that point, and it is about learner evidence, not about performance.
 */

export const queryKeys = {
  health: ['health'] as const,
  identity: ['identity'] as const,
  profile: ['profile'] as const,
  courses: ['courses'] as const,
  // #497: connected or not, and when. Never the token: it is in no response and no key.
  moodleConnection: ['moodle', 'connection'] as const,
  /** The Moodle account's name from this session's connect result: client state only, never fetched and never stored. */
  moodleConnectedAs: ['moodle', 'connected-as'] as const,
  authSession: ['auth', 'session'] as const,
  // #585: everything kept about the signed-in learner, read on demand and held in memory only (§6.2).
  myData: ['me', 'data'] as const,
  consentStatement: ['consent', 'statement'] as const,
  consentDecisions: ['consent', 'decisions'] as const,
  progressSummary: ['progress', 'summary'] as const,
  courseTopics: (courseRef: string) => ['progress', 'course-topics', courseRef] as const,
  assessments: ['assessments'] as const,
  practisableActivities: ['practisable-activities'] as const,
  usage: ['usage'] as const,
  // #440: exam prep. Read responses held in memory only (§6.2).
  examPrepAssessments: ['exam-prep', 'assessments'] as const,
  examPrepPlan: (planId: string) => ['exam-prep', 'plan', planId] as const,
  examPrepSession: (sessionId: string) => ['exam-prep', 'session', sessionId] as const,
  imports: ['imports'] as const,
  // #376: an upload's pages, and one page's transcription. Both are read
  // responses held in memory only (§6.2); the text in them is learner work.
  homeworkList: ['homework', 'list'] as const,
  homeworkExercises: ['homework', 'exercises'] as const,
  homeworkUpload: (uploadId: string) => ['homework', 'upload', uploadId] as const,
  homeworkTranscription: (pageId: string) => ['homework', 'transcription', pageId] as const,
  // #377: the CLOSED feedback read (GET). What an opening POST shows is never cached here: it lives in the screen's
  // memory only, so nothing re-shows help the server did not record showing.
  homeworkFeedback: (uploadId: string) => ['homework', 'feedback', uploadId] as const,
  // #469: the triage (computed by a POST, never cached across uploads) and the feedback channel (polled while pending).
  homeworkTriage: (uploadId: string) => ['homework', 'triage', uploadId] as const,
  homeworkFeedbackChannel: (uploadId: string) => ['homework', 'feedback-channel', uploadId] as const,
  objectiveStatus: (objectiveId: string) => ['progress', 'objective', objectiveId, 'status'] as const,
  objectiveEvidence: (objectiveId: string) =>
    ['progress', 'objective', objectiveId, 'evidence'] as const,
} as const;
